import { apiFetch } from './client';
import type { Role } from '@/lib/auth-store';

export interface UserRow {
  id: string; ref_number: string | null; email: string; role: Role;
  reseller_id: string | null; customer_id: string | null;
  display_name: string | null; phone: string | null;
  active: boolean; mfa_enabled: boolean; last_login_at: string | null;
  must_change_password: boolean; created_at: string;
}

export interface CreateUserBody {
  email: string;
  role: Role;
  resellerId: string | null;
  customerId: string | null;
  displayName: string | null;
  phone: string | null;
  method: 'email_invite' | 'manual_password';
  manualPassword?: string;
}

export const users = {
  list: (p: { resellerId?: string; customerId?: string; role?: Role; search?: string } = {}) => {
    const q = new URLSearchParams();
    if (p.resellerId) q.set('reseller_id', p.resellerId);
    if (p.customerId) q.set('customer_id', p.customerId);
    if (p.role) q.set('role', p.role);
    if (p.search) q.set('search', p.search);
    return apiFetch<{ rows: UserRow[] }>(`/api/users?${q}`);
  },
  /**
   * `id` may be a UUID, `USR-221`, or bare `221` (rule 28 — human URLs).
   * `customerId` disambiguates bare refs for wide-scope viewers (SA/RO):
   * user refs are per-scope, so the same number can exist in two customers.
   */
  get: (id: string, customerId?: string | null) =>
    apiFetch<UserRow>(
      `/api/users/${id}${customerId ? `?customer_id=${customerId}` : ''}`,
    ),
  create: (body: CreateUserBody) =>
    apiFetch<{ userId: string | null; inviteId: string | null; inviteToken: string | null }>(
      '/api/users', { method: 'POST', body: JSON.stringify(body) },
    ),
  update: (id: string, body: Partial<Pick<UserRow, 'display_name' | 'phone' | 'active' | 'email'>> & { role?: Role }) =>
    apiFetch<UserRow>(`/api/users/${id}`, { method: 'PATCH', body: JSON.stringify({
      displayName: body.display_name, phone: body.phone, active: body.active, role: body.role,
      email: body.email,
    }) }),
  remove: (id: string) => apiFetch<void>(`/api/users/${id}`, { method: 'DELETE' }),
  resendInvite: (id: string) => apiFetch<{ inviteToken: string }>(`/api/users/${id}/resend-invite`, { method: 'POST' }),
  adminResetPassword: (id: string, tempPassword: string) =>
    apiFetch<{ ok: true }>(`/api/users/${id}/reset-password-admin`, {
      method: 'POST', body: JSON.stringify({ temp_password: tempPassword }),
    }),
  sendPasswordResetEmail: (id: string) =>
    apiFetch<{ ok: true }>(`/api/users/${id}/send-password-reset-email`, { method: 'POST' }),
  setRecordingPin: (pin: string) =>
    apiFetch<{ ok: true }>('/api/users/me/recording-pin', {
      method: 'PUT', body: JSON.stringify({ pin }),
    }),
  removeRecordingPin: () =>
    apiFetch<{ ok: true }>('/api/users/me/recording-pin', { method: 'DELETE' }),
};
